import { ErrorBoundary } from 'react-error-boundary';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';

// Layouts
import AnalyticsGrid from '@/components/layouts/wrappers/AnalyticsGrid';
import { StatsWrapper } from '@/components/pages/analyze/StatsWrapper';
import { AnalyticsWrapper } from '@/components/layouts/wrappers/AnalyticsWrapper';
import { StatsGroupWrapper } from '@/components/pages/analyze-group/StatsGroupWrapper';
import HeaderAnalytics from '@/components/layouts/wrappers/LayoutAnalytics/HeaderAnalytics';
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';

// Hooks
import useAnalyticsPeriod from '@/utils/hooks/analytics/usePeriodAnalytics';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

// Stores
import { useDatepickerGlobalStore } from '@/utils/zustand/providers/datepicker-global-provider';

// Lib
import { LAYOUT_ANALYTICS_TYPES } from '@/lib/analytics/global';
import { PENDO_CORE_ANALYTICS } from '@/lib/tools/pendo/events';

// Components
import AdsPermissionsBanner from '@/components/composite/Banners/AdsPermissionsBanner';
import { SocialProfilesGroupFirstStatsBanner } from '@/components/composite/Banners/SocialProfilesGroupFirstStatsBanner';
import { pendoTrack } from '@/components/tools/Pendo';
import { PageHeaderRecap } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap';
import { PageContentWrapper } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper';

const ContentSocialProfileAnalytics = ({ config, periodSelected }) => {
    return (
        <>
            <div className="col-span-full">
                <AdsPermissionsBanner className="mb-10" />
            </div>
            <AnalyticsWrapper
                periodStart={periodSelected.start}
                periodEnd={periodSelected.end}
                compareTo={periodSelected.compareTo}
                period={periodSelected.period}>
                <StatsWrapper pageConfig={config} />
            </AnalyticsWrapper>
        </>
    );
};

const ContentGroupAnalytics = ({ config, periodSelected }) => {
    return (
        <>
            <div className="col-span-full mb-10">
                <SocialProfilesGroupFirstStatsBanner />
            </div>
            <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback}>
                <AnalyticsGrid>
                    <StatsGroupWrapper pageConfig={config} periodSelected={periodSelected} />
                </AnalyticsGrid>
            </ErrorBoundary>
        </>
    );
};

export function LayoutAnalyticsTreeStructure({ title, profileConfig, groupConfig }) {
    const { t } = useTranslation();
    const isGroup = useIsGroup();

    const { date, period, compareTo } = useDatepickerGlobalStore((state) => state);
    const periodSelected = {
        date: [date.from, date.to],
        start: dayjs(date.from).format('YYYY-MM-DD'),
        end: dayjs(date.to).format('YYYY-MM-DD'),
        period,
        compareTo
    };

    const config = isGroup ? groupConfig : profileConfig;

    return (
        <>
            <PageHeaderRecap title={t(title)} />
            <PageContentWrapper>
                <div className="grid grid-cols-1 gap-x-3 md:grid-cols-[1fr_16.5rem]">
                    {isGroup ? (
                        <ContentGroupAnalytics config={config} periodSelected={periodSelected} />
                    ) : (
                        <ContentSocialProfileAnalytics
                            config={config}
                            periodSelected={periodSelected}
                        />
                    )}
                </div>
            </PageContentWrapper>
        </>
    );
}

const LayoutAnalytics = ({
    config,
    title,
    withCoreEventPendo = false,
    type = LAYOUT_ANALYTICS_TYPES.DEFAULT
}) => {
    const { periodSelected, setPeriodSelected } = useAnalyticsPeriod(type);

    const isGroupAnalytics = useIsGroup();

    useEffect(() => {
        if (!withCoreEventPendo) return;
        pendoTrack(PENDO_CORE_ANALYTICS);
    }, [withCoreEventPendo]);

    return (
        <div className="grid grid-cols-1 gap-x-3 md:grid-cols-[1fr_16.5rem]">
            <HeaderAnalytics
                isGroup={isGroupAnalytics}
                title={title}
                type={type}
                periodSelected={periodSelected}
                setPeriodSelected={setPeriodSelected}
            />
            {isGroupAnalytics ? (
                <ContentGroupAnalytics config={config} periodSelected={periodSelected} />
            ) : (
                <ContentSocialProfileAnalytics config={config} periodSelected={periodSelected} />
            )}
        </div>
    );
};

export default LayoutAnalytics;
